import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { api } from './api'
import { AUFRISSSTUFE, einzug, rangklasse, Zeilenbeschriftung } from './Bericht'
import { t } from './sprache'
import type { Aufrisszelle, Bericht as Berichtssatz, Berichtszeile, Periodenbericht } from './typen'

/**
 * Die GuV-Gliederung unter dem Periodenergebnis der Kapitalflussrechnung
 * (**R489**, 2.8 Abschnitt 13.19).
 *
 * **Auftrag des Auftraggebers vom 2026-09-13:** *„Das Periodenergebnis in der
 * Kapitalflussrechnung soll beim Aufklappen die gesamte GuV-Struktur
 * darstellen, damit das Ergebnis und Cross-Check bei dem Wiederrausrechnen
 * (z. B. AfA) per Sichtabgleich möglich ist."*
 *
 * **Warum das etwas anderes ist als ein Aufriss.** Ein Aufriss zerlegt eine
 * Zahl entlang einer Achse — nach Konten, nach Gesellschaften, nach Partnern —
 * und liefert eine *flache* Liste je Ebene. Die indirekte Darstellung braucht
 * das Gegenteil: die **Gliederung** des § 275 HGB mit ihren Zwischensummen,
 * weil der Sichtabgleich an genau diesen Zwischensummen hängt. Wer die
 * Abschreibung in Zeile 2 wieder herausrechnet, will sie darunter in der GuV
 * stehen sehen — und darunter das Betriebsergebnis, in das sie eingegangen
 * ist.
 *
 * **Es ist die GuV desselben Laufs**, nicht eine zweite Rechnung. Die
 * Ergebniszeile der GuV (R484) und das Periodenergebnis der
 * Kapitalflussrechnung sind dieselbe Zahl; stimmen sie nicht überein, ist das
 * genau der Befund, für den der Sichtabgleich da ist.
 *
 * **Leere Posten bleiben stehen.** Die Anzeigeoption *leere Zeilen ausblenden*
 * greift hier ausdrücklich nicht: Ein Sichtabgleich, dem eine erwartete
 * Position fehlt, lässt offen, ob sie null ist oder übersehen wurde — und das
 * ist die eine Frage, die er beantworten soll (7.3 Abschnitt 11, Ausnahme).
 */

/**
 * Der Schlüssel, unter dem die GuV-Gliederung im Aufklappzustand steht.
 *
 * **Sie ist keine Achse im Sinne von `Aufriss.ts`** — es gibt keine Zellen mit
 * diesem Merkmal und nichts nachzuladen. Sie benutzt denselben Zustand, weil
 * derselbe Pfeil sie auf- und zuklappt und derselbe Verweis sie mitnimmt
 * (R156): Ein zweiter Merker neben dem Aufklappzustand wäre für den Anwender
 * dieselbe Sache mit zwei Verhalten.
 */
export const ACHSE_GUV = 'guv'

/**
 * Die Konzern-GuV **über den Zeitraum der Kapitalflussrechnung** — und nicht
 * die des angezeigten Laufs (R489).
 *
 * **Das ist der Punkt, an dem der Sichtabgleich steht oder fällt.** Eine
 * Kapitalflussrechnung ist eine Stromgröße über einen Zeitraum; ein Lauf
 * gehört einer Periode. Die GuV des Septemberlaufs unter eine Rechnung für
 * Januar bis September zu stellen, sähe aus wie ein Abgleich und wäre keiner —
 * am Demokonzern gemessen (2026-09-13): 147.799,33 aus dem Septemberlauf neben
 * einem Periodenergebnis von −89.500,03 über neun Monate.
 *
 * **Eine GuV ist addierbar, eine Bilanz nicht.** Deshalb geht es hier so
 * einfach: Die Rechnung nennt in `grundlage` jeden Lauf, den sie summiert hat
 * (hier neun); ihre GuV-Zeilen werden Spalte für Spalte aufaddiert, und
 * heraus kommt die GuV genau dieses Zeitraums. Kein zweiter Rechengang, keine
 * zweite Wahrheit — dieselben festgeschriebenen Läufe, aus denen die
 * Kapitalflussrechnung selbst besteht.
 *
 * **Geholt wird erst beim Aufklappen.** Neun Berichte für eine Zeile, die
 * niemand geöffnet hat, wären neun Abfragen für nichts. `anfordern` stößt den
 * Abruf an, wenn die Gliederung zum ersten Mal gezeichnet wird.
 */
export function useErgebnisgliederung(
  grundlage: readonly { laufId: number }[] | undefined,
  plan: string | null | undefined,
): { zeilen: Berichtszeile[] | null; anfordern: () => void } {
  const [zeilen, setZeilen] = useState<Berichtszeile[] | null>(null)

  // Woraus die gehaltene Summe entstanden ist. Ändert sich der Zeitraum, die
  // Gliederung oder ein Lauf, ist sie die Antwort auf eine andere Frage.
  const kennung = `${plan ?? ''}|${(grundlage ?? []).map((g) => g.laufId).join(',')}`
  const geholt = useRef<string | null>(null)

  useEffect(() => {
    if (geholt.current !== null && geholt.current !== kennung) {
      geholt.current = null
      setZeilen(null)
    }
  }, [kennung])

  const anfordern = useCallback(() => {
    if (geholt.current === kennung || !grundlage || grundlage.length === 0) {
      return
    }

    geholt.current = kennung
    Promise.all(grundlage.map((g) => api.bericht(g.laufId, 'guv', plan || undefined)))
      .then((berichte) => setZeilen(summiert(berichte)))
      .catch(() => {
        // Ein gescheiterter Abruf darf nicht dauerhaft blockieren: Der nächste
        // Aufklappversuch fragt neu.
        geholt.current = null
      })
  }, [kennung, grundlage, plan])

  return { zeilen, anfordern }
}

/**
 * Die GuV **je Spalte der Periodenansicht** (2.8 Abschnitt 13.33, R1105).
 *
 * **Jede Spalte der Kapitalflussrechnung ist eine eigene Rechnung mit eigenem
 * Zeitraum** — bis zum 2026-10-05 war das der Grund, die Gliederung dort nicht
 * anzubieten (R489). Der Periodenbericht der GuV hat aber genau dieselben
 * Spalten: denselben Zeitraum, dasselbe Raster, dieselbe Einstellung
 * *kumuliert seit Jahresbeginn*, unter einer Gesellschaft dieselbe gelieferte
 * Sicht. Wer ihn holt, bestimmt der Aufrufer (`laden`); `kennung` sagt, auf
 * welche Frage die gehaltene Antwort passt.
 *
 * **Geholt wird erst beim Aufklappen**, wie oben.
 */
export function useGuvJeSpalte(
  laden: (() => Promise<Periodenbericht>) | undefined,
  kennung: string,
): { bericht: Periodenbericht | null; anfordern: () => void } {
  const [bericht, setBericht] = useState<Periodenbericht | null>(null)
  const geholt = useRef<string | null>(null)

  useEffect(() => {
    if (geholt.current !== null && geholt.current !== kennung) {
      geholt.current = null
      setBericht(null)
    }
  }, [kennung])

  const anfordern = useCallback(() => {
    if (!laden || geholt.current === kennung) {
      return
    }

    const fuer = kennung
    geholt.current = fuer
    laden()
      .then((b) => {
        // Eine Antwort auf die vorige Frage bleibt liegen.
        if (geholt.current === fuer) {
          setBericht(b)
        }
      })
      .catch(() => {
        geholt.current = null
      })
  }, [laden, kennung])

  return { bericht, anfordern }
}

/**
 * Mehrere GuV-Berichte zu einem addieren — Zeile für Zeile, Spalte für
 * Spalte.
 *
 * Die Gliederung nimmt der **letzte** Bericht vor: Er steht dem Stichtag am
 * nächsten, und wenn ein Posten unterwegs dazugekommen ist, soll er dastehen.
 * Eine Zeile, die es nur früher gab, bleibt trotzdem erhalten — sie trägt
 * einen Betrag, und ein Betrag ohne Zeile wäre verloren.
 */
function summiert(berichte: Berichtssatz[]): Berichtszeile[] {
  const summe = new Map<string, Berichtszeile>()
  const felder = ['hb2', 'kapKo', 'schuKo', 'aeKo', 'zwErgEUv', 'zwErgEAv', 'konzern'] as const

  for (const bericht of berichte) {
    for (const zeile of bericht.zeilen) {
      const bisher = summe.get(zeile.position)
      if (!bisher) {
        summe.set(zeile.position, { ...zeile, beitraege: [...zeile.beitraege] })
        continue
      }

      // Die jüngere Fassung gibt Beschriftung, Ebene und Rang vor, die ältere
      // nur ihre Zahlen.
      const neu: Berichtszeile = { ...zeile }
      for (const f of felder) {
        neu[f] = bisher[f] + zeile[f]
      }

      neu.beitraege = beitraegeSumme(bisher.beitraege, zeile.beitraege)
      summe.set(zeile.position, neu)
    }
  }

  // In der Reihenfolge des letzten Berichts, und was nur früher vorkam, hinten
  // dran: Eine Zeile ohne Platz in der Gliederung ist besser als eine Zahl,
  // die niemand sieht.
  const letzter = berichte[berichte.length - 1]?.zeilen ?? []
  const bekannt = new Set(letzter.map((z) => z.position))

  return [
    ...letzter.map((z) => summe.get(z.position)!).filter(Boolean),
    ...[...summe.values()].filter((z) => !bekannt.has(z.position)),
  ]
}

/** Die Aufrisszellen zweier Berichte, je Gesellschaft und Konto addiert. */
function beitraegeSumme(a: Aufrisszelle[], b: Aufrisszelle[]): Aufrisszelle[] {
  const nach = new Map<string, Aufrisszelle>()

  for (const z of [...a, ...b]) {
    const schluessel = `${z.einheit ?? ''}|${z.konto ?? ''}`
    const bisher = nach.get(schluessel)
    if (!bisher) {
      nach.set(schluessel, { ...z })
      continue
    }

    nach.set(schluessel, {
      ...bisher,
      hb2: bisher.hb2 + z.hb2,
      kapKo: bisher.kapKo + z.kapKo,
      schuKo: bisher.schuKo + z.schuKo,
      aeKo: bisher.aeKo + z.aeKo,
      zwErgEUv: bisher.zwErgEUv + z.zwErgEUv,
      zwErgEAv: bisher.zwErgEAv + z.zwErgEAv,
      konzern: bisher.konzern + z.konzern,
    })
  }

  return [...nach.values()]
}

/** Was diese Gliederung von einer Zeile braucht — Bilanz, GuV und Periodenbericht erfüllen es. */
export type Gliederungszeile = {
  position: string
  code: string
  namen: Record<string, string>
  ebene: number
  istSumme: boolean
  istErgebnis: boolean
}

/** Stößt das Nachladen an, sobald die Gliederung zum ersten Mal gezeichnet wird. */
function Anforderung({ anfordern }: { anfordern: () => void }) {
  useEffect(() => {
    anfordern()
  }, [anfordern])

  return null
}

export function Ergebnisgliederung<Z extends Gliederungszeile>({
  zeilen,
  anfordern,
  spaltenzahl,
  vorspalte,
  werte,
}: {
  zeilen: Z[] | null
  /** Holt die GuV des Zeitraums — gerufen, sobald die Gliederung dasteht. */
  anfordern?: () => void
  spaltenzahl: number
  /**
   * Eine Zelle **vor** der Bezeichnung — die Nummernspalte der
   * Kapitalflussrechnung. Ohne sie rutschte jede Zeile um eine Spalte nach
   * links (dieselbe Vorkehrung wie in `Aufrisszeilen`).
   */
  vorspalte?: React.ReactNode
  /** Die Wertzellen einer Zeile — je Spaltenbild andere. */
  werte: (zeile: Z, id: string) => React.ReactNode
}) {
  // **Noch nicht da heißt nicht leer.** Die GuV wird mit dem Lauf geholt; auf
  // dem Bildschirm der Kapitalflussrechnung kann sie einen Wimpernschlag
  // später eintreffen. Eine Zeile, die das sagt, ist besser als eine
  // Gliederung, die stillschweigend fehlt.
  if (zeilen === null) {
    return (
      <tr className="beitrag aufrissmeldung">
        {anfordern && <Anforderung anfordern={anfordern} />}
        {vorspalte}
        <td colSpan={spaltenzahl - (vorspalte ? 1 : 0)} style={{ paddingLeft: einzug(0, AUFRISSSTUFE) }}>
          {t('aufriss.laedt')}
        </td>
      </tr>
    )
  }

  if (zeilen.length === 0) {
    return (
      <tr className="beitrag aufrissmeldung">
        {vorspalte}
        <td colSpan={spaltenzahl - (vorspalte ? 1 : 0)} style={{ paddingLeft: einzug(0, AUFRISSSTUFE) }}>
          {t('aufriss.leer')}
        </td>
      </tr>
    )
  }

  return (
    <>
      {zeilen.map((zeile) => {
        const id = `guv:${zeile.position}`

        return (
          <Fragment key={zeile.position}>
            {/*
              **`guvgliederung`, nicht `gliederungszeile`.** Unter dem zweiten
              Namen steht im Stilblatt der Eintrag im Inhaltsverzeichnis des
              Anhangs — ein Knopf mit `display: flex`. Die Zeile übernahm ihn
              und hörte auf, eine Tabellenzeile zu sein: Die Beträge standen
              auf Ziffernbreite zusammengedrückt unter dem Periodenergebnis
              (Befund des Auftraggebers vom 2026-09-30, fehlerhaft seit dem
              2026-09-13).
            */}
            <tr
              className={[
                'beitrag',
                'guvgliederung',
                rangklasse(zeile),
              ]
                .join(' ')
                .trim()}
            >
              {vorspalte}
              {/*
                Die Treppe der GuV setzt auf der Aufrissstufe auf: Die ganze
                Gliederung steht eine Stufe unter ihrer Zeile, und darin
                behält jeder Posten die Ebene, die er in der GuV hat.
              */}
              <td style={{ paddingLeft: einzug(zeile.ebene, AUFRISSSTUFE) }}>
                <Zeilenbeschriftung zeile={zeile} />
              </td>
              {werte(zeile, id)}
            </tr>
          </Fragment>
        )
      })}
    </>
  )
}
